/**
 * Research: what a civilisation knows, what it is learning, and what it costs.
 *
 * Science comes out of commerce. Every city's commerce — the gold yield of the tiles it
 * works — is split by the civilisation's science rate: that share accumulates here and
 * the rest reaches the treasury. So research is paid for in gold not collected, which is
 * the trade-off the slider exists to expose (ADR-0042).
 *
 * Three rules shape everything below, and each exists to stop the player losing work:
 *
 *   SCIENCE IS NEVER WASTED. Unspent science sits in `researchStore`. Changing research
 *   keeps it; having nothing chosen keeps it. Nothing the player does throws it away.
 *
 *   ONE DISCOVERY A TURN. Whatever is left over after a discovery carries to the next
 *   one, but a large store completes technologies one turn at a time rather than in a
 *   burst the player cannot follow.
 *
 *   GOALS, NOT ONLY CHOICES. With eighty technologies, choosing each by hand is a chore.
 *   A civilisation can aim at anything in the tree and the engine walks the way there,
 *   cheapest ready prerequisite first. The AI uses the same machinery.
 *
 * Cost is `baseCost × costGrowth^depth`, computed by repeated multiplication rather than
 * `**` — IEEE multiplication is exactly rounded everywhere, `Math.pow` is only
 * approximately specified, and a cost that differs in the last bit between two machines
 * is a save hash that differs too.
 */

import type { Content, TechnologyDef } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { budget } from '../cities/economy.js';
import { maxScienceRate } from '../government/index.js';
import { NONE, type Civ, type GameState, type Id } from '../state/types.js';
import { knows, unlocksOf, type Unlock } from './knowledge.js';
import type { AiWeights } from '../ai/personality.js';

export { hasTech, knows, unlocksOf, type Unlock } from './knowledge.js';

// ─── cost ──────────────────────────────────────────────────────────────────────

export function techCost(content: Content, techId: number): number {
  const rules = content.rules.research;
  const depth = content.technologies.get(techId).depth;
  let cost = rules.baseCost;
  // Steep through the early tree, gentler past `lateDepth` (Phase 13a): with one rate for
  // every depth the late technologies outran any economy and Flight came at turn 450.
  for (let d = 0; d < depth; d++) cost *= d < rules.lateDepth ? rules.costGrowth : rules.lateGrowth;
  return Math.max(1, Math.round(cost));
}

// ─── knowledge ─────────────────────────────────────────────────────────────────

/** Not yet known, and every prerequisite is. */
export function canResearch(content: Content, civ: Civ, techId: number): boolean {
  if (knows(civ, techId)) return false;
  return content.technologies.get(techId).prerequisites.every((p) => knows(civ, p));
}

/** Everything researchable right now, cheapest first, then in tree order. */
export function availableTechs(content: Content, civ: Civ): TechnologyDef[] {
  return content.technologies.all
    .filter((t) => canResearch(content, civ, t.id))
    .sort((a, b) => techCost(content, a.id) - techCost(content, b.id) || a.id - b.id);
}

export function learn(civ: Civ, techId: number): void {
  if (knows(civ, techId)) return;
  civ.techs = [...civ.techs, techId].sort((a, b) => a - b);
}

/**
 * Every unknown technology standing between a civilisation and `goal`, the goal
 * included, in an order it could research them.
 *
 * A topological walk over just the unknown part of the goal's ancestry, always taking
 * the cheapest technology whose own prerequisites are already satisfied — so the path is
 * deterministic, and it spends early turns on cheap steps rather than stalling on an
 * expensive one that could have waited.
 */
export function pathTo(content: Content, civ: Civ, goal: number): number[] {
  if (knows(civ, goal)) return [];

  const needed = new Set<number>();
  const collect = (id: number): void => {
    if (needed.has(id) || knows(civ, id)) return;
    needed.add(id);
    for (const p of content.technologies.get(id).prerequisites) collect(p);
  };
  collect(goal);

  const path: number[] = [];
  const done = new Set<number>();
  while (done.size < needed.size) {
    let best = NONE;
    for (const id of needed) {
      if (done.has(id)) continue;
      const ready = content.technologies.get(id).prerequisites.every((p) => knows(civ, p) || done.has(p));
      if (!ready) continue;
      if (best === NONE || techCost(content, id) < techCost(content, best) || (techCost(content, id) === techCost(content, best) && id < best)) {
        best = id;
      }
    }
    // Unreachable only if content validation let a cycle through, which it does not.
    if (best === NONE) break;
    done.add(best);
    path.push(best);
  }
  return path;
}

/** What to research next on the way to the civilisation's goal, or NONE. */
export function nextTowardGoal(content: Content, civ: Civ): number {
  if (civ.researchGoal === NONE) return NONE;
  return pathTo(content, civ, civ.researchGoal)[0] ?? NONE;
}

/** Turns until the current research completes at this rate of science, or null if never. */
export function turnsToDiscover(content: Content, civ: Civ, sciencePerTurn: number): number | null {
  if (civ.researching === NONE) return null;
  const remaining = techCost(content, civ.researching) - civ.researchStore;
  if (remaining <= 0) return 1;
  if (sciencePerTurn <= 0) return null;
  return Math.ceil(remaining / sciencePerTurn);
}

/** Nothing being researched, nothing aimed at, and something that could be. */
export function researchIdle(content: Content, civ: Civ): boolean {
  return civ.researching === NONE && civ.researchGoal === NONE && availableTechs(content, civ).length > 0;
}

/** The latest era any known technology belongs to — the civilisation's era. */
export function currentEra(content: Content, civ: Civ): number {
  let era = 0;
  for (const id of civ.techs) era = Math.max(era, content.technologies.get(id).eraIndex);
  return era;
}

// ─── the rate ──────────────────────────────────────────────────────────────────

/**
 * The highest science rate this civilisation can run without its treasury shrinking.
 *
 * Zero if even that loses money. Used by the AI every turn, and once when an old save
 * written before research existed is loaded — otherwise its players would find half
 * their gold gone and their buildings being sold, for a slider they had never seen.
 */
export function maxSolventRate(state: GameState, content: Content, civId: Id): number {
  const rules = content.rules.research;
  for (let rate = maxScienceRate(state, content, civId); rate > 0; rate -= rules.rateStep) {
    if (budget(state, content, civId, rate).net >= 0) return rate;
  }
  return 0;
}

// ─── the turn ──────────────────────────────────────────────────────────────────

/**
 * Put this turn's science into research, and discover at most one technology.
 *
 * With nothing chosen, the next step toward the goal is taken; with no goal either,
 * science simply accumulates until the player chooses.
 */
export function advanceResearch(state: GameState, content: Content, civId: Id, science: number): GameEvent[] {
  const civ = state.civs[civId];
  if (!civ) return [];

  civ.researchStore += Math.max(0, science);

  if (civ.researching === NONE) civ.researching = nextTowardGoal(content, civ);
  if (civ.researching === NONE) return [];

  const cost = techCost(content, civ.researching);
  if (civ.researchStore < cost) return [];

  const tech = civ.researching;
  const eraBefore = currentEra(content, civ);
  civ.researchStore -= cost;
  learn(civ, tech);
  civ.researching = NONE;
  if (civ.researchGoal !== NONE && knows(civ, civ.researchGoal)) civ.researchGoal = NONE;
  // Line up the next step now, so the panel shows what is coming rather than a gap.
  civ.researching = nextTowardGoal(content, civ);

  const events: GameEvent[] = [{ t: 'TechDiscovered', civId, tech, name: content.technologies.get(tech).name }];
  const eraAfter = currentEra(content, civ);
  if (eraAfter > eraBefore) {
    events.push({ t: 'EraEntered', civId, era: eraAfter, name: content.eras.get(eraAfter).name });
  }
  return events;
}

/**
 * How much a technology is worth researching next, lower being better: its cost,
 * discounted by what it unlocks. Each unlock counts by its kind — soldiers, science,
 * gold, growth — at the weight the civilisation's personality gives that kind
 * (ADR-0053), so a militarist reaches for bronze and a scientist for writing. With
 * every kind at 1 this is AI-0's rule exactly: a technology that opens nothing is only
 * chosen when it is cheap.
 */
export function researchScore(content: Content, techId: number, weights: AiWeights = content.rules.ai): number {
  let value = 0;
  for (const unlock of unlocksOf(content, techId)) value += unlockWeight(content, unlock, weights);
  return techCost(content, techId) / (1 + weights.techUnlockWeight * value);
}

function unlockWeight(content: Content, unlock: Unlock, weights: AiWeights): number {
  if (unlock.kind === 'improvement') return weights.techGrowthWeight;
  if (unlock.kind === 'government') return weights.techGovernmentWeight;
  if (unlock.kind === 'resource') return weights.techMilitaryWeight * 0.5;
  if (unlock.kind === 'unit') {
    const def = content.units.get(unlock.id);
    return def.attack > 0 || def.defence > 0 ? weights.techMilitaryWeight : 1;
  }
  const effects = content.buildings.get(unlock.id).effects;
  if (effects.sciencePercent) return weights.techScienceWeight;
  if (effects.goldBonus) return weights.techGoldWeight;
  if (effects.veteranUnits) return weights.techMilitaryWeight;
  if (effects.growthFoodKept || effects.sizeCapIncrease) return weights.techGrowthWeight;
  return 1;
}
